import React, { useState, useEffect } from 'react';
import { FormSelect } from '../../../FormSelect';
import { FormInput } from '../../../FormInput';

/**
 * Step 1: Document Requirement Information
 * 
 * Purpose: Establish context and select which document template to complete
 * 
 * Captures:
 * - Template selection (required)
 * - Club context
 * - Team context (if applicable)
 * - Participant context (if applicable)
 */
const Step1_RequirementInfo = ({
  wizardData,
  templates = [],
  clubs = [],
  teams = [],
  participants = [],
  onDataChange,
  onValidationChange
}) => {
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    // Validate that a template is selected
    const newErrors = {};
    if (!wizardData.templateId) {
      newErrors.templateId = 'Please select a document template';
    }
    
    onValidationChange(Object.keys(newErrors).length === 0);
    setErrors(newErrors);
  }, [wizardData.templateId, onValidationChange]);

  const handleChange = (field, value) => {
    onDataChange({
      ...wizardData,
      [field]: value
    });
  };

  // Get selected template for display
  const selectedTemplate = templates.find(t => t.id === wizardData.templateId);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Select Document Template</h3>
        <FormSelect
          label="Template"
          name="templateId"
          value={wizardData.templateId || ''}
          onChange={(e) => handleChange('templateId', e.target.value)}
          options={templates.map(t => ({
            value: t.id,
            label: t.title || t.name
          }))}
          error={errors.templateId}
          required
          placeholder="Choose a document template to complete..."
        />
      </div>

      {selectedTemplate && (
        <>
          {/* Template Description & Mode Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Template Information</h4>
            <p className="text-sm text-blue-800">{selectedTemplate.description || selectedTemplate.instructions}</p>
            
            {/* Show acceptance mode info */}
            <div className="mt-3 pt-3 border-t border-blue-200">
              {selectedTemplate.acceptance_mode === 'accept' ? (
                <p className="text-xs text-blue-700">
                  <span className="font-semibold">Type:</span> This is a signing document. You will need to provide your signature.
                </p>
              ) : (
                <p className="text-xs text-blue-700">
                  <span className="font-semibold">Type:</span> This document requires your acknowledgment/acceptance.
                </p>
              )}
            </div>
            
            {selectedTemplate.created_at && (
              <p className="text-xs text-blue-700 mt-2">
                Created: {new Date(selectedTemplate.created_at).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Club Context */}
          <div>
            <h4 className="font-semibold mb-3">Document Context</h4>
            <div className="space-y-4">
              {clubs.length > 0 && (
                <FormSelect
                  label="Club"
                  name="club_id"
                  value={wizardData.club_id || ''}
                  onChange={(e) => handleChange('club_id', e.target.value)}
                  options={[
                    { value: '', label: 'Select a club...' },
                    ...clubs.map(c => ({
                      value: c.id,
                      label: c.name
                    }))
                  ]}
                  required
                />
              )}

              {teams.length > 0 && (
                <FormSelect
                  label="Team"
                  name="teamId"
                  value={wizardData.teamId || ''}
                  onChange={(e) => handleChange('teamId', e.target.value)}
                  options={[
                    { value: '', label: 'Select a team...' },
                    ...teams.map(t => ({
                      value: t.id,
                      label: t.name
                    }))
                  ]}
                />
              )}

              {participants.length > 0 && (
                <FormSelect
                  label="Participant"
                  name="participantId"
                  value={wizardData.participantId || ''}
                  onChange={(e) => handleChange('participantId', e.target.value)}
                  options={[
                    { value: '', label: 'Select a participant...' },
                    ...participants.map(p => ({
                      value: p.id,
                      label: p.name
                    }))
                  ]}
                />
              )}
            </div>
          </div>

          {/* Optional: Additional Context Fields */}
          {wizardData.templateId && (
            <div>
              <h4 className="font-semibold mb-3">Additional Information</h4>
              <FormInput
                label="Tracking Reference ID (Optional)"
                name="trackingReferenceId"
                value={wizardData.trackingReferenceId || ''}
                onChange={(e) => handleChange('trackingReferenceId', e.target.value)}
                placeholder="e.g., REQ-2024-001"
                helpText="Optional reference number for tracking purposes"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { Step1_RequirementInfo };
